import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { AdminPage, Loading } from 'components';
import { Alert, AlertType } from 'components/lds';
import { H3, H4, S } from 'components/typography';

import { Breadcrumbs } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeBreadcrumbs';
import { SectionCard } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeSectionCard';
import { InspectionTemplateCustomizeSidebar } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeSidebar';
import { baselineBackedCustomizeSectionIncludedCount } from '../helpers/inspectionTemplateCustomizeHelpers';
import { useInspectionTemplateCustomizePage } from '../hooks/inspectionTemplateCustomize/useInspectionTemplateCustomizePage';
import styles from './InspectionTemplateCustomizePage.scss';

type Props = {
  channelBasePath: string;
};

export function InspectionTemplateCustomizePage({ channelBasePath }: Props) {
  const { t } = useTranslation();

  const {
    activeSectionId,
    called,
    error,
    expandedBySectionId,
    handleCancel,
    hasValidRouteContext,
    inspectionsTemplateLibraryEnabled,
    lastUpdatedText,
    listPageUrl,
    loading,
    mainColumnRefCallback,
    removeItemDisabledTitle,
    removeSectionDisabledTitle,
    removeTemplateItem,
    removeTemplateSection,
    removedOverlayItemIds,
    removedSectionPresentationIds,
    removingItemIds,
    removingSectionIds,
    restoreTemplateItem,
    restoreTemplateSection,
    restoringItemIds,
    restoringSectionIds,
    revertAllDisabledTitle,
    saveDisabledTitle,
    sortedSections,
    summary,
    baselineTemplate,
    template: inspectionTemplate,
    toggleSection,
  } = useInspectionTemplateCustomizePage(channelBasePath);

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      removeTemplateItem(itemId).catch(() => {});
    },
    [removeTemplateItem]
  );

  const handleRestoreItem = useCallback(
    (itemId: string) => {
      restoreTemplateItem(itemId).catch(() => {});
    },
    [restoreTemplateItem]
  );

  const handleRemoveSection = useCallback(
    (sectionId: string) => {
      removeTemplateSection(sectionId).catch(() => {});
    },
    [removeTemplateSection]
  );

  const handleRestoreSection = useCallback(
    (sectionId: string) => {
      restoreTemplateSection(sectionId).catch(() => {});
    },
    [restoreTemplateSection]
  );

  if (!hasValidRouteContext) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Alert type={AlertType.error} fullWidth>
            {t('web.admin.inspections.templateCustomize.missingContext')}
          </Alert>
        </div>
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.page}>
          <Breadcrumbs listPageUrl={listPageUrl} />
          <Alert
            type={AlertType.error}
            fullWidth
            className={styles.alertSpacing}
          >
            {t('web.admin.inspections.templateCustomize.error')}
          </Alert>
          <Button variant="secondary" size="large" onClick={handleCancel}>
            {t('web.admin.inspections.templateCustomize.cancel')}
          </Button>
        </div>
      </AdminPage>
    );
  }

  if ((loading || !called) && !inspectionTemplate) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  if (called && !loading && !inspectionTemplate) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.page}>
          <Breadcrumbs listPageUrl={listPageUrl} />
          <Alert
            type={AlertType.error}
            fullWidth
            className={styles.alertSpacing}
          >
            {t('web.admin.inspections.templateCustomize.notFound')}
          </Alert>
          <Button variant="secondary" size="large" onClick={handleCancel}>
            {t('web.admin.inspections.templateCustomize.cancel')}
          </Button>
        </div>
      </AdminPage>
    );
  }

  if (!inspectionTemplate) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.page}>
        <Breadcrumbs listPageUrl={listPageUrl} />

        <div className={styles.pageTitle}>
          <H3>{inspectionTemplate.metadata.name}</H3>
          <S variant="secondary">
            {t('web.admin.inspections.templateCustomize.lastUpdated', {
              date: lastUpdatedText,
            })}
          </S>
        </div>

        <div className={styles.contentRow}>
          <div ref={mainColumnRefCallback} className={styles.mainColumn}>
            <div className={styles.alertWrap}>
              <Alert type={AlertType.info} fullWidth className={styles.alertBanner}>
                {t('web.admin.inspections.templateCustomize.instanceOnlyNotice')}
              </Alert>
            </div>

            <div className={styles.questionsHeadingRow}>
              <div className={styles.questionsHeadingText}>
                <H4>
                  {t('web.admin.inspections.templateCustomize.questionsHeading')}
                </H4>
                <S variant="secondary">{summary}</S>
              </div>
              <span title={revertAllDisabledTitle}>
                <Button
                  variant="secondary"
                  size="large"
                  disabled
                  aria-disabled
                  startIcon={
                    <Icon
                      name="undo"
                      set={ICON_SET_FONTAWESOME}
                      type="far"
                    />
                  }
                >
                  {t('web.admin.inspections.templateCustomize.revertAll')}
                </Button>
              </span>
            </div>

            {sortedSections.map((section, sectionIndex) => (
              <SectionCard
                key={section.id}
                section={section}
                sectionIndex={sectionIndex}
                expanded={expandedBySectionId[section.id] ?? true}
                onToggle={() => toggleSection(section.id)}
                isSectionRemovedPresentation={removedSectionPresentationIds.has(
                  section.id
                )}
                sectionRemoveDisabled={false}
                removeSectionDisabledTitle={removeSectionDisabledTitle}
                removeItemDisabledTitle={removeItemDisabledTitle}
                onRemoveSection={
                  removedSectionPresentationIds.has(section.id)
                    ? undefined
                    : () => handleRemoveSection(section.id)
                }
                isRemoveSectionLoading={removingSectionIds.has(section.id)}
                onRestoreSection={
                  inspectionsTemplateLibraryEnabled &&
                  removedSectionPresentationIds.has(section.id)
                    ? () => handleRestoreSection(section.id)
                    : undefined
                }
                isRestoreSectionLoading={
                  restoringSectionIds.has(section.id)
                }
                restoreSectionDisabled={Boolean(
                  removingSectionIds.size > 0 ||
                    restoringItemIds.size > 0 ||
                    removingItemIds.size > 0
                )}
                restoreSectionDisabledTitle={
                  removingSectionIds.size > 0 ||
                  restoringItemIds.size > 0 ||
                  removingItemIds.size > 0
                    ? t(
                        'web.admin.inspections.templateCustomize.restoreSectionBlockedHint'
                      )
                    : ''
                }
                onRemoveItem={handleRemoveItem}
                removingItemIds={removingItemIds}
                onRestoreItem={handleRestoreItem}
                restoringItemIds={restoringItemIds}
                removedOverlayItemIds={removedOverlayItemIds}
                baselineBackedDraftIncludedItemCount={
                  baselineBackedCustomizeSectionIncludedCount(
                    section.id,
                    baselineTemplate,
                    inspectionTemplate
                  )
                }
              />
            ))}
          </div>

          <InspectionTemplateCustomizeSidebar
            sections={sortedSections}
            activeSectionId={activeSectionId}
            removedSectionPresentationIds={removedSectionPresentationIds}
          />
        </div>

        <div className={styles.footer}>
          <span title={saveDisabledTitle}>
            <Button variant="primary" size="large" disabled aria-disabled>
              {t('web.admin.inspections.templateCustomize.save')}
            </Button>
          </span>
          <Button variant="text" size="large" onClick={handleCancel}>
            {t('web.admin.inspections.templateCustomize.cancel')}
          </Button>
        </div>
      </div>
    </AdminPage>
  );
}
